import { getFirestore, collection, query, getDocs, where, orderBy, doc, getDoc } from "firebase/firestore";
import { app } from "../config";
import { isDemoMode } from "../../demo/isDemoMode";
import demoStore from "../../demo/demoStore";

const db = getFirestore(app);

// Helper to convert Firestore Timestamp or date string to Date object
const parseDate = (value) => {
    if (!value) return null;
    if (value.toDate && typeof value.toDate === 'function') {
        return value.toDate();
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
};

// Function to fetch all events
const fetchAllEvents = async () => {
    try {
        const eventsRef = collection(db, "events");
        const q = query(
            eventsRef,
            orderBy("createdAt", "desc") // Sort by creation date, newest first
        );

        const querySnapshot = await getDocs(q);
        const events = [];

        querySnapshot.forEach((doc) => {
            const eventData = doc.data();

            // Convert Firestore Timestamps to JavaScript Dates
            const event = {
                id: doc.id,
                name: eventData.eventName,
                date: parseDate(eventData.startDate),
                location: eventData.location,
                applicationDeadline: parseDate(eventData.deadlineDate),
                sellerCapacity: eventData.sellerCapacity,
                status: "Open", // You might want to add a status field to your event creation
                description: eventData.description,
                startTime: eventData.startTime,
                endDate: parseDate(eventData.endDate),
                endTime: eventData.endTime,
                loadInDate: parseDate(eventData.loadInDate),
                loadInTime: eventData.loadInTime,
                loadoutDate: parseDate(eventData.loadoutDate),
                loadoutTime: eventData.loadoutTime,
                sellerTypes: eventData.sellerTypes,
                socialMediaHandles: eventData.socialMediaHandles,
                // Add any other fields you need
            };
            events.push(event);
        });

        return events;
    } catch (error) {
        console.error("Error fetching events: ", error);
        return [];
    }
};



// Function to fetch all published events
const fetchAllPublishedEvents = async () => {
    try {
        if (isDemoMode) {
            // Demo mode: read from demoStore
            const allEvents = demoStore.getDocs('events', [
                { type: 'where', field: 'isDraft', op: '==', value: false }
            ]);

            const events = allEvents.map(({ id, data: eventData }) => ({
                id,
                eventName: eventData.eventName,
                eventCoverPhoto: eventData.eventCoverPhoto,
                eventType: eventData.eventType,
                startDate: parseDate(eventData.startDate),
                endDate: parseDate(eventData.endDate),
                applicationDeadline: parseDate(eventData.deadlineDate),
                location: eventData.location,
                city: eventData.city,
                state: eventData.state,
                description: eventData.description,
                eventDescription: eventData.description,
                sellerCapacity: eventData.sellerCapacity,
                status: "Open",
                startTime: eventData.startTime,
                endTime: eventData.endTime,
                loadInDate: parseDate(eventData.loadInDate),
                loadInTime: eventData.loadInTime,
                loadoutDate: parseDate(eventData.loadoutDate),
                loadoutTime: eventData.loadoutTime,
                sellerTypes: eventData.sellerTypes,
                socialMediaHandles: eventData.socialMediaHandles,
            }));

            // Sort by createdAt descending
            events.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                return dateB - dateA;
            });

            return events;
        }

        // Production: read from Firestore
        const eventsRef = collection(db, "events");
        const q = query(
            eventsRef,
            where("isDraft", "==", false),
            orderBy("createdAt", "desc") // Sort by creation date, newest first
        );

        const querySnapshot = await getDocs(q);
        const events = [];

        querySnapshot.forEach((docItem) => {
            const eventData = docItem.data();

            // Convert Firestore Timestamps to JavaScript Dates
            const event = {
                id: docItem.id,
                eventName: eventData.eventName,
                eventCoverPhoto: eventData.eventCoverPhoto,
                eventType: eventData.eventType,
                startDate: parseDate(eventData.startDate),
                endDate: parseDate(eventData.endDate),
                applicationDeadline: parseDate(eventData.deadlineDate),
                location: eventData.location,
                city: eventData.city,
                state: eventData.state,
                description: eventData.description,
                eventDescription: eventData.description,
                sellerCapacity: eventData.sellerCapacity,
                status: "Open",
                startTime: eventData.startTime,
                endTime: eventData.endTime,
                loadInDate: parseDate(eventData.loadInDate),
                loadInTime: eventData.loadInTime,
                loadoutDate: parseDate(eventData.loadoutDate),
                loadoutTime: eventData.loadoutTime,
                sellerTypes: eventData.sellerTypes,
                socialMediaHandles: eventData.socialMediaHandles,
            };
            events.push(event);
        });

        return events;
    } catch (error) {
        console.error("Error fetching events: ", error);
        return [];
    }
};



// Function to fetch events for a specific date
const fetchEventsByDate = async (date) => {
    try {
        const eventsRef = collection(db, "events");
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        const q = query(
            eventsRef,
            where("isDraft", "==", false),
            where("startDate", ">=", startOfDay.toISOString()),
            where("startDate", "<=", endOfDay.toISOString())
        );

        const querySnapshot = await getDocs(q);
        const events = [];

        querySnapshot.forEach((doc) => {
            const eventData = doc.data();
            events.push({
                id: doc.id,
                name: eventData.eventName,
                date: parseDate(eventData.startDate),
                location: eventData.location,
                status: "Open",
                // Add other relevant fields
            });
        });

        return events;
    } catch (error) {
        console.error("Error fetching events by date: ", error);
        return [];
    }
};

// Function to fetch recommended events (you can customize the criteria)
const fetchRecommendedEvents = async (userLocation = null, limit = 10) => {
    try {
        const eventsRef = collection(db, "events");
        let q = query(
            eventsRef,
            where("isDraft", "==", false),
            orderBy("createdAt", "desc"),
            // Add more query constraints based on your recommendation criteria
            // For example: where("location", "==", userLocation) if location matching is needed
            // where("startDate", ">=", new Date().toISOString()) for upcoming events only
        );

        const querySnapshot = await getDocs(q);
        const events = [];

        querySnapshot.forEach((doc) => {
            const eventData = doc.data();
            events.push({
                id: doc.id,
                name: eventData.eventName,
                date: parseDate(eventData.startDate),
                location: eventData.location,
                applicationDeadline: parseDate(eventData.deadlineDate),
                sellerCapacity: eventData.sellerCapacity,
                status: "Open",
                // Add other fields as needed
            });
        });

        return events;
    } catch (error) {
        console.error("Error fetching recommended events: ", error);
        return [];
    }
};

//get Published event data by ID
const fetchPublishedEventById = async (eventId) => {
    console.log("Fetching published event by ID: ", eventId);
    try {
        if (isDemoMode) {
            // Demo mode: read from demoStore
            const eventData = demoStore.getDoc('events', eventId);
            if (eventData) {
                return eventData;
            } else {
                console.log("No such document in demoStore!");
                return null;
            }
        }

        // Production: read from Firestore
        const eventRef = doc(db, "events", eventId); // Reference to the specific document by ID
        const docSnapshot = await getDoc(eventRef);

        if (docSnapshot.exists()) {
            const eventData = docSnapshot.data();
            return eventData;
        } else {
            console.log("No such document!");
            return null; // Return null if the document does not exist
        }
    } catch (error) {
        console.error("Error fetching event by ID: ", error);
        return null;
    }
};

const fetchEventsForMPS = async (userID, userLocation = null, limit = 10) => {
    try {

        //fetch recommended events 
        const eventsRef = collection(db, "events");
        let q = query(
            eventsRef,
            where("isDraft", "==", false),
            orderBy("createdAt", "desc"),
            // Add more query constraints based on your recommendation criteria
            // For example: where("location", "==", userLocation) if location matching is needed
            // where("startDate", ">=", new Date().toISOString()) for upcoming events only
        );

        const querySnapshot = await getDocs(q);
        const events = [];
        querySnapshot.forEach((doc) => {
            const eventData = doc.data();
            events.push({
                id: doc.id,
                image: eventData.eventCoverPhoto,
                name: eventData.eventName,
                date: parseDate(eventData.startDate),
                location: eventData.location,
                applicationDeadline: parseDate(eventData.deadlineDate),
                sellerCapacity: eventData.sellerCapacity,
                status: "Open",
                // Add other fields as needed
            });
        });

        // fetch events  MPS registered
        const eventRegistrationRef = collection(db, "mpsEventRegistration");
        const userQuery = query(eventRegistrationRef, where("userId", "==", userID));
        const queryAppliedSnapshot = await getDocs(userQuery);
        const eventDetails = queryAppliedSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                eventId: data.eventId,
                applicationStatus: data.applicationStatus,
                paymentStatus: data.paymentStatus,
            };
        });
        console.log(events)
        //update events adding status
        const updatedEvents = events.map((event) => {
            const appliedEvent = eventDetails.find(
                (applied) => applied.eventId === event.id
            );
            return {
                ...event,
                status: appliedEvent ? appliedEvent.applicationStatus : "Not Applied",
            };
        });

        return updatedEvents;
    } catch (error) {
        console.error("Error fetching recommended events: ", error);
        return [];
    }
}

export { fetchAllEvents, fetchEventsByDate, fetchRecommendedEvents, fetchAllPublishedEvents, fetchPublishedEventById, fetchEventsForMPS };